import React, { useState } from 'react';
import { abonosAPI } from '../services/api';

const formatSoles = (amount) => new Intl.NumberFormat('es-PE', {
  style: 'currency',
  currency: 'PEN'
}).format(Number(amount) || 0);

const AbonoForm = ({ expedienteId, expediente, abonos = [], onConfirmFullPayment, onAbonoCreated, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useEvidence, setUseEvidence] = useState(false);
  
  const [formData, setFormData] = useState({
    fecha: '',
    monto: '',
    metodoPago: 'Transferencia Bancaria'
  });

  const [evidenceFile, setEvidenceFile] = useState(null);
  const montoAPagar = Number(
    expediente?.montoAPagar ??
    (Number(expediente?.montoTotal || 0) + Number(expediente?.totalExtras || 0))
  );
  const totalAbonado = Array.isArray(abonos)
    ? abonos.reduce((sum, abono) => sum + Number(abono.monto || 0), 0)
    : Number(expediente?.totalAbonado || 0);
  const saldoRestante = Number.isFinite(montoAPagar)
    ? Math.max(0, Math.round((montoAPagar - totalAbonado) * 100) / 100)
    : null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setEvidenceFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validaciones b├ísicas
      if (!formData.fecha || !formData.monto) {
        throw new Error('Todos los campos son obligatorios');
      }

      const montoNumerico = parseFloat(formData.monto);

      if (montoNumerico <= 0) {
        throw new Error('El monto debe ser mayor a 0');
      }

      if (saldoRestante != null && montoNumerico > saldoRestante) {
        throw new Error(`El monto excede el saldo pendiente (${formatSoles(saldoRestante)})`);
      }

      if (saldoRestante != null && Math.abs(montoNumerico - saldoRestante) < 0.005) {
        const mensaje = `Vas a registrar un abono por ${formatSoles(montoNumerico)}, que cubre todo el saldo pendiente del expediente. ¿Confirmas que ese es el monto correcto?`;
        const confirmado = onConfirmFullPayment
          ? await onConfirmFullPayment(mensaje)
          : window.confirm(mensaje);

        if (!confirmado) {
          return;
        }
      }

      let response;

      // Si incluye evidencia (comprobante), usar endpoint con archivo
      if (useEvidence && evidenceFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('fecha', formData.fecha);
        formDataToSend.append('monto', montoNumerico);
        formDataToSend.append('metodoPago', formData.metodoPago);
        formDataToSend.append('evidencia', evidenceFile);

        response = await abonosAPI.createAbonoConEvidencia(expedienteId, formDataToSend);
        console.log('✅ Abono con comprobante creado:', response.data);
      } else {
        // Sin evidencia: enviar YYYY-MM-DD; el backend interpreta el día civil en Perú
        const abonoData = {
          monto: montoNumerico,
          fecha: formData.fecha,
          metodoPago: formData.metodoPago,
          generarRecibo: true
        };

        response = await abonosAPI.createAbonoConRecibo(expedienteId, abonoData);
        
        // Mostrar mensaje de éxito con info del recibo
        if (response.data?.reciboGenerado) {
          console.log('✅ Recibo generado:', response.data.reciboGenerado);
        }
      }
      
      // Limpiar formulario
      setFormData({
        fecha: '',
        monto: '',
        metodoPago: 'Transferencia Bancaria'
      });
      setEvidenceFile(null);
      setUseEvidence(false);
      
      // Notificar al componente padre
      if (onAbonoCreated) {
        onAbonoCreated();
      }
    } catch (err) {
      console.error('Error al crear abono:', err);
      
      // Manejar diferentes tipos de errores
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.details) {
        // Si hay detalles de validaci├│n, mostrar el primer error
        const firstError = err.response.data.details[0];
        setError(firstError.msg || 'Error de validaci├│n');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Error al crear el abono');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <div className="modal-title">
            <i className="fas fa-credit-card modal-icon"></i>
            <h3>Registrar Nuevo Abono</h3>
          </div>
          <button 
            className="modal-close" 
            onClick={onCancel}
            type="button"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="modal-body">
          {error && (
            <div className="modal-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
          )}

          {saldoRestante != null && (
            <div className="modal-info" style={{ marginBottom: '1rem' }}>
              <i className="fas fa-info-circle"></i>
              <span>Saldo pendiente: {formatSoles(saldoRestante)}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="fecha" className="form-label">
                <i className="fas fa-calendar-alt"></i>
                Fecha del Abono *
              </label>
              <input
                type="date"
                id="fecha"
                name="fecha"
                className="form-control modal-input"
                value={formData.fecha}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="monto" className="form-label">
                <i className="fas fa-money-bill-wave"></i>
                Monto *
              </label>
              <input
                type="number"
                id="monto"
                name="monto"
                className="form-control modal-input"
                value={formData.monto}
                onChange={handleInputChange}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0.00"
                step="0.01"
                min="0"
                max={saldoRestante != null ? saldoRestante : undefined}
                autoComplete="off"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="metodoPago" className="form-label">
                <i className="fas fa-credit-card"></i>
                Método de Pago
              </label>
              <select
                id="metodoPago"
                name="metodoPago"
                className="form-control modal-input"
                value={formData.metodoPago}
                onChange={handleInputChange}
                required
              >
                <option value="Transferencia Bancaria">Transferencia Bancaria</option>
                <option value="Depósito Bancario">Depósito Bancario</option>
                <option value="Efectivo">Efectivo</option>
                <option value="Plin">Plin</option>
                <option value="Yape">Yape</option>
                <option value="Billetera Digital">Billetera Digital</option>
                <option value="Cheque">Cheque</option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label checkbox-label">
                <input
                  type="checkbox"
                  checked={useEvidence}
                  onChange={(e) => setUseEvidence(e.target.checked)}
                  className="modal-checkbox"
                />
                <i className="fas fa-file-upload"></i>
                Incluir comprobante
              </label>
            </div>

            {useEvidence && (
              <div className="form-group">
                <label htmlFor="evidencia" className="form-label">
                  <i className="fas fa-file-alt"></i>
                  Comprobante de Pago
                </label>
                <input
                  type="file"
                  id="evidencia"
                  name="evidencia"
                  className="form-control modal-input file-input"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.gif"
                />
                <small className="form-text">
                  <i className="fas fa-info-circle"></i>
                  Formatos permitidos: PDF, JPG, PNG, GIF (m├íximo 10MB)
                </small>
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn-modal btn-cancel"
                onClick={onCancel}
                disabled={loading}
              >
                <i className="fas fa-times"></i>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-modal btn-submit"
                disabled={loading}
              >
                <i className="fas fa-check"></i>
                {loading ? 'Registrando...' : 'Registrar Abono'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AbonoForm;



